/** The plan catalogue, as the owner supplied it (2026-08-22).
 *
 *  There is no free plan and no trial: the product is purely paid (owner, 2026-08-22). Prospective users get a
 *  separate seeded demo experience rather than a trial of the real thing, so nothing here models one — a nullable
 *  `trialDays` nobody sets would be a field the next reader has to work out is dead.
 *
 *  This lives in contracts rather than in the seed or the sim because three consumers must agree on it exactly: the
 *  seed writes it, the sim serves it, and the Billing screen renders it. It is *not* demo data — these are the real
 *  prices and limits — so it does not belong under `@pointfive/sim`, where everything is labelled simulated.
 *
 *  These are real product figures, not demo values — the tiers, prices, account limits and selling points are quoted
 *  from the pricing page. The 20% yearly discount is applied per month rather than as an annual total, because that is
 *  how the catalogue states it and how the screen shows it.
 *
 *  `highlights` are the increments over the tier below; the "Everything in X" line comes from `inherits`, so nothing is
 *  written twice and a change to a lower tier propagates upward by construction.
 *
 *  `features` is a different list and deliberately so: it is the entitlement keys the kernel cascade checks at step 03.
 *  Copy and authority drift apart the moment they share a field.
 */

export interface SeedPlan {
  readonly key: string;
  readonly name: string;
  readonly tier: 'free' | 'starter' | 'trader' | 'pro';
  /** Monthly price when billed monthly. The yearly price is derived — see `YEARLY_DISCOUNT`. */
  readonly monthly: number;
  readonly blurb: string;
  readonly highlights: readonly string[];
  readonly inherits: string | null;
  readonly recommended: boolean;
  readonly accountSlots: number;
  readonly voiceMinutes: number;
  readonly aiDailyBudget: number;
  readonly features: readonly string[];
}

/** Paying for a year charges 20% less per month, rounded **down** to a whole dollar (owner, 2026-08-22):
 *  $29 → $23, $59 → $47, $99 → $79. Rounding down rather than to nearest means the customer never pays more than the
 *  headline discount implies — the effective saving is a shade over 20%, never under it. */
export const YEARLY_DISCOUNT = 0.2;
export const monthlyWhenYearly = (monthly: number): number => Math.floor(monthly * (1 - YEARLY_DISCOUNT));

export const SEED_PLANS: readonly SeedPlan[] = [
  {
    key: 'starter',
    name: 'Starter',
    tier: 'starter',
    monthly: 29,
    blurb: 'For traders building the habit — real analytics, the playbook defined, and deeper guardrails.',
    // Starter is now the entry tier, so it carries the base features outright rather than inheriting them from a
    // plan that no longer exists (owner, 2026-08-22: "remove the free plan").
    highlights: [
      '2 account connections',
      'Trading-style profile — the context for all AI analysis',
      'Start My Day ritual & daily limits',
      'Journal with screenshots & tags',
      'Main dashboard & P&L calendar',
      'Native TradingView charting',
      'Trading lockouts & drawdown alerts',
      'Core analytics & CSV export',
      'Strategies playbook & trade tagging',
      'Risk profile — quiet hours & the cool-off rule',
      'History import — MT4, MT5, TradeZella',
    ],
    inherits: null,
    recommended: false,
    accountSlots: 2,
    voiceMinutes: 60,
    aiDailyBudget: 60,
    features: [
      'dashboard',
      'journal',
      'review',
      'style',
      'charting',
      'lockouts',
      'analytics',
      'strategies',
      'riskprofile',
      'import',
    ],
  },
  {
    key: 'trader',
    name: 'Trader',
    tier: 'trader',
    monthly: 59,
    blurb:
      'For active traders who want the rules enforced for them — automation armed, behaviour watched, replays graded.',
    highlights: [
      '3 account connections',
      'Automatic trade management — breakeven, trailing, profit lock',
      'Execution checklists & red-flag detection',
      'Trade Replay with AI grading',
      'AI end-of-day reports & digests',
    ],
    inherits: 'Starter',
    recommended: true,
    accountSlots: 3,
    voiceMinutes: 120,
    aiDailyBudget: 200,
    features: [
      'dashboard',
      'journal',
      'review',
      'style',
      'charting',
      'lockouts',
      'analytics',
      'strategies',
      'riskprofile',
      'import',
      'automation',
      'checklists',
      'replay',
      'eod',
      'digest',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    tier: 'pro',
    monthly: 99,
    blurb:
      'For professionals running an operation — full AI, prop-challenge tooling, outside capital handled properly.',
    highlights: [
      '5 account connections, add-ons available',
      'Full AI Insights with numbered action plans',
      'Predictive success score & challenge cockpit',
      'Compare Accounts & widget dashboard',
      'Copy trading — ratio, fixed lot, or risk-matched',
      'Investor portal, agreements & payment ledger',
      'FIX connections & priority support',
    ],
    inherits: 'Trader',
    recommended: false,
    accountSlots: 5,
    voiceMinutes: 600,
    aiDailyBudget: 1000,
    features: [
      'dashboard',
      'journal',
      'review',
      'style',
      'charting',
      'lockouts',
      'analytics',
      'strategies',
      'riskprofile',
      'import',
      'automation',
      'checklists',
      'replay',
      'eod',
      'digest',
      'analytics.pro',
      'insights',
      'predictive',
      'compare',
      'widgets.premium',
      'copy',
      'investors',
      'portal',
      'fix',
      'signals',
      'voice',
      'backtest',
    ],
  },
];
